import { useState } from 'react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  Link2,
  Copy,
  ExternalLink,
  Plus,
  QrCode,
  TrendingUp,
  Users,
  MousePointer,
  MoreHorizontal,
  Trash2,
  Edit,
} from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

interface ReferralLink {
  id: string
  code: string
  campaign: string
  url: string
  clicks: number
  registrations: number
  conversions: number
  isDefault: boolean
  createdAt: string
}

const mockLinks: ReferralLink[] = [
  {
    id: '1',
    code: 'AFF001',
    campaign: 'ลิงก์หลัก',
    url: 'https://winner99.com/ref/AFF001',
    clicks: 3542,
    registrations: 128,
    conversions: 3.61,
    isDefault: true,
    createdAt: '2025-01-15',
  },
  {
    id: '2',
    code: 'AFF001-FB',
    campaign: 'Facebook',
    url: 'https://winner99.com/ref/AFF001-FB',
    clicks: 1250,
    registrations: 45,
    conversions: 3.6,
    isDefault: false,
    createdAt: '2025-03-20',
  },
  {
    id: '3',
    code: 'AFF001-LINE',
    campaign: 'LINE',
    url: 'https://winner99.com/ref/AFF001-LINE',
    clicks: 890,
    registrations: 38,
    conversions: 4.27,
    isDefault: false,
    createdAt: '2025-04-10',
  },
  {
    id: '4',
    code: 'AFF001-TT',
    campaign: 'TikTok',
    url: 'https://winner99.com/ref/AFF001-TT',
    clicks: 2100,
    registrations: 65,
    conversions: 3.1,
    isDefault: false,
    createdAt: '2025-05-05',
  },
]

export function AffiliateLinks() {
  const [links] = useState(mockLinks)
  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const [showQrDialog, setShowQrDialog] = useState(false)
  const [selectedLink, setSelectedLink] = useState<ReferralLink | null>(null)

  const copyLink = (url: string) => {
    navigator.clipboard.writeText(url)
  }

  const totalClicks = links.reduce((acc, link) => acc + link.clicks, 0)
  const totalRegistrations = links.reduce((acc, link) => acc + link.registrations, 0)
  const avgConversion = (totalRegistrations / totalClicks * 100).toFixed(2)

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">ลิงก์แนะนำ</h1>
          <p className="text-muted-foreground">จัดการลิงก์สำหรับแนะนำสมาชิก</p>
        </div>
        <Button className="gap-2" onClick={() => setShowCreateDialog(true)}>
          <Plus className="h-4 w-4" />
          สร้างลิงก์ใหม่
        </Button>
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-primary/10">
                <Link2 className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">ลิงก์ทั้งหมด</p>
                <p className="text-2xl font-bold">{links.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-blue-500/10">
                <MousePointer className="h-5 w-5 text-blue-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">คลิกทั้งหมด</p>
                <p className="text-2xl font-bold">{totalClicks.toLocaleString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-win/10">
                <Users className="h-5 w-5 text-win" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">สมัครทั้งหมด</p>
                <p className="text-2xl font-bold">{totalRegistrations}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-amber-500/10">
                <TrendingUp className="h-5 w-5 text-amber-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Conversion Rate</p>
                <p className="text-2xl font-bold">{avgConversion}%</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Links List */}
      <div className="grid gap-4">
        {links.map((link) => (
          <Card key={link.id}>
            <CardContent className="p-4">
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                <div className="flex items-center gap-4 flex-1">
                  <div className="p-3 rounded-lg bg-primary/10">
                    <Link2 className="h-6 w-6 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold">{link.campaign}</h3>
                      {link.isDefault && (
                        <Badge variant="secondary" className="text-[10px]">ค่าเริ่มต้น</Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <code className="text-xs bg-muted px-2 py-1 rounded truncate max-w-xs">
                        {link.url}
                      </code>
                      <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => copyLink(link.url)}>
                        <Copy className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-6">
                  <div className="text-center">
                    <p className="text-lg font-bold">{link.clicks.toLocaleString()}</p>
                    <p className="text-xs text-muted-foreground">คลิก</p>
                  </div>
                  <div className="text-center">
                    <p className="text-lg font-bold">{link.registrations}</p>
                    <p className="text-xs text-muted-foreground">สมัคร</p>
                  </div>
                  <div className="text-center">
                    <p className="text-lg font-bold text-win">{link.conversions}%</p>
                    <p className="text-xs text-muted-foreground">Conversion</p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="gap-1"
                    onClick={() => {
                      setSelectedLink(link)
                      setShowQrDialog(true)
                    }}
                  >
                    <QrCode className="h-4 w-4" />
                    QR
                  </Button>
                  <Button variant="outline" size="sm" className="gap-1">
                    <ExternalLink className="h-4 w-4" />
                    เปิด
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem className="gap-2">
                        <Edit className="h-4 w-4" /> แก้ไข
                      </DropdownMenuItem>
                      <DropdownMenuItem className="gap-2 text-destructive">
                        <Trash2 className="h-4 w-4" /> ลบ
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Create Link Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>สร้างลิงก์ใหม่</DialogTitle>
            <DialogDescription>สร้างลิงก์แนะนำสำหรับแคมเปญต่างๆ</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>ชื่อแคมเปญ</Label>
              <Input placeholder="เช่น Facebook, LINE, TikTok" />
            </div>
            <div className="space-y-2">
              <Label>รหัสลิงก์ (ต่อท้าย)</Label>
              <div className="flex gap-2">
                <div className="flex-1 p-3 rounded-lg bg-muted text-sm">
                  https://winner99.com/ref/AFF001-
                </div>
                <Input placeholder="CUSTOM" className="w-32" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              ยกเลิก
            </Button>
            <Button className="gap-2">
              <Plus className="h-4 w-4" />
              สร้างลิงก์
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* QR Code Dialog */}
      <Dialog open={showQrDialog} onOpenChange={setShowQrDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>QR Code</DialogTitle>
            <DialogDescription>{selectedLink?.campaign}</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col items-center py-6">
            <div className="w-48 h-48 bg-white rounded-lg flex items-center justify-center border">
              <QrCode className="h-32 w-32 text-black" />
            </div>
            <p className="text-xs text-muted-foreground mt-4 text-center">
              {selectedLink?.url}
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" className="w-full gap-2">
              <Copy className="h-4 w-4" />
              ดาวน์โหลด QR Code
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}










